<template>
  <div>
    <!-- Lista de condiciones -->
    <v-list v-if="localConditions.length > 0" class="py-0">
      <v-list-item
        v-for="(condition, index) in localConditions"
        :key="index"
        class="px-0"
      >
        <v-list-item-content>
          <v-card outlined class="pa-3">
            <v-row>
              <!-- Operador lógico (AND/OR) - excepto en la primera condición -->
              <v-col cols="12" v-if="index > 0">
                <v-btn-toggle
                  v-model="condition.logic"
                  @change="updateConditions"
                  mandatory
                  dense
                  class="mb-2"
                >
                  <v-btn
                    v-for="logic in logicOperators"
                    :key="logic.logic"
                    :value="logic.logic"
                    small
                  >
                    {{ logic.ui.label }}
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small right v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span>{{ logic.ui.description }}</span>
                    </v-tooltip>
                  </v-btn>
                </v-btn-toggle>
              </v-col>
              <!-- Campo -->
              <v-col cols="12" md="4">
                <v-select
                  v-model="condition.field"
                  :items="availableFields"
                  label="Campo"
                  item-text="ui.label"
                  item-value="field"
                  outlined
                  dense
                  @change="onFieldChange(condition)"
                >
                  <template v-slot:item="{ item }">
                    <span class="font-mono">{{ item.ui.label }}</span>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span class="font-mono">{{ item.ui.label }}</span>
                  </template>
                </v-select>
              </v-col>

              <!-- Operador -->
              <v-col cols="12" md="3">
                <v-select
                  v-model="condition.operator"
                  :items="getAvailableOperators(condition.field)"
                  item-text="ui.label"
                  item-value="operator"
                  label="Operador"
                  outlined
                  dense
                  @change="updateConditions"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.ui.symbol }} {{ item.ui.label }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ item.ui.description }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template v-slot:selection="{ item }">
                    {{ item.ui.symbol }} {{ item.ui.label }}
                  </template>
                </v-select>
              </v-col>

              <!-- Valor -->
              <v-col cols="12" md="4">
                <div>
                  <CustomField
                    :filter="getInputConfig(condition.field)"
                    v-model="condition.value"
                    @change="updateConditions"
                  />
                </div>
              </v-col>

              <!-- Botón eliminar -->
              <v-col
                cols="12"
                md="1"
                class="d-flex align-center justify-center"
              >
                <v-btn icon color="error" @click="removeCondition(index)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <!-- Preview de la condición -->
            <v-divider class="my-2"></v-divider>
            <div class="text-caption text--secondary">
              <v-icon x-small class="mr-1">mdi-code-braces</v-icon>
              <span class="font-mono">
                {{ getConditionPreview(condition, index) }}
              </span>
            </div>
          </v-card>
        </v-list-item-content>
      </v-list-item>
    </v-list>

    <!-- Mensaje cuando no hay condiciones -->
    <v-alert v-else type="info" text dense class="mb-4">
      No hay condiciones configuradas. El webhook se ejecutará en todos los
      eventos.
    </v-alert>

    <!-- Botón agregar condición -->
    <v-btn
      color="primary"
      outlined
      @click="addCondition"
      :disabled="availableFields.length === 0"
    >
      <v-icon left>mdi-plus</v-icon>
      Agregar condición
    </v-btn>

    <!-- Mensaje de ayuda si no hay campos disponibles -->
    <v-alert
      v-if="availableFields.length === 0"
      type="warning"
      text
      dense
      class="mt-3"
    >
      Selecciona primero una entidad y evento para poder agregar condiciones.
    </v-alert>

    <!-- Preview general de todas las condiciones -->
    <v-card v-if="localConditions.length > 1" outlined class="mt-4 pa-3">
      <v-subheader class="px-0">Preview de la lógica completa</v-subheader>
      <div class="text-body-2 font-mono">
        <div v-for="(condition, index) in localConditions" :key="index">
          <span v-if="index > 0" class="font-weight-bold text-primary">
            {{ condition.logic?.toUpperCase() || "AND" }}
          </span>
          {{ condition.field }}
          {{ getOperatorSymbol(condition.operator) }}
          "{{ condition.value }}"
        </div>
      </div>
    </v-card>
    {{ localConditions }}
  </div>
</template>

<script>
import CustomField from "../../../layouts/filters/components/CustomField.vue";
export default {
  name: "WebhookConditionsBuilder",
  components: {
    CustomField
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    availableFields: {
      type: Array,
      default: () => []
    },
    operators: {
      type: Array,
      default: () => []
    },
    logicOperators: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localConditions: []
    };
  },
  mounted() {
    this.initializeConditions();
  },
  watch: {
    availableFields() {
      // Los campos pueden cargar después de las condiciones guardadas
      const normalized = this.localConditions.map(condition =>
        this.normalizeValue(condition.field, condition.value)
      );
      let changed = false;
      this.localConditions.forEach((condition, i) => {
        if (condition.value !== normalized[i]) {
          condition.value = normalized[i];
          changed = true;
        }
      });
      if (changed) this.updateConditions();
    },
    value: {
      handler(newVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(this.localConditions)) {
          this.initializeConditions();
        }
      },
      deep: true
    }
  },
  methods: {
    initializeConditions() {
      this.localConditions =
        this.value.length > 0
          ? JSON.parse(JSON.stringify(this.value)).map(condition => ({
              ...condition,
              value: this.normalizeValue(condition.field, condition.value)
            }))
          : [];
    },
    isMultipleField(field) {
      const data = this.availableFields.find(f => f.field === field);
      return (
        !!data &&
        (data.type === "array" ||
          (data.type === "string" && !!(data.values && data.values.length)))
      );
    },
    normalizeValue(field, value) {
      if (!this.isMultipleField(field) || Array.isArray(value)) return value;
      return value === undefined || value === null || value === ""
        ? []
        : [value];
    },
    addCondition() {
      const newCondition = {
        field: this.availableFields[0].field || "",
        operator: this.availableFields[0].available_operators[0] || "equals",
        value: "",
        logic: "and"
      };
      newCondition.value = this.normalizeValue(newCondition.field, "");
      this.localConditions.push(newCondition);
      this.updateConditions();
    },
    onFieldChange(condition) {
      condition.value = this.normalizeValue(condition.field, "");
      this.updateConditions();
    },
    removeCondition(index) {
      this.localConditions.splice(index, 1);
      this.updateConditions();
    },
    updateConditions() {
      this.$emit("input", [...this.localConditions]);
    },
    getOperatorSymbol(operatorValue) {
      const operator = this.operators.find(op => op.operator === operatorValue);
      return operator?.ui?.symbol || "=";
    },
    getOperatorLabel(operatorValue) {
      const operator = this.operators.find(op => op.operator === operatorValue);
      return operator?.ui?.label || operatorValue;
    },
    getConditionPreview(condition, index) {
      const logicPrefix =
        index > 0 ? `${condition.logic?.toUpperCase() || "AND"} ` : "";
      const operator = this.getOperatorSymbol(condition.operator);
      const field = condition.field || "[campo]";
      const value =
        condition.value !== undefined && condition.value !== ""
          ? `"${condition.value}"`
          : "[valor]";

      return `${logicPrefix}${field} ${operator} ${value}`;
    },
    getAvailableOperators(field) {
      return (
        this.availableFields
          .find(f => f.field === field)
          ?.available_operators.map(opValue =>
            this.operators.find(op => op.operator === opValue)
          ) || []
      );
    },
    getInputConfig(field) {
      const data = this.availableFields.find(f => f.field === field);

      if (!data)
        return {
          type: "string",
          label: "Campo no disponible",
          values: []
        };
      return {
        type: data.type,
        label: data.ui.label,
        values: data.values || []
      };
    }
  }
};
</script>

<style scoped>
.font-mono {
  font-family: "Monaco", "Menlo", "Ubuntu Mono", "Consolas", monospace;
  font-size: 0.9em;
}

.v-list-item {
  margin-bottom: 12px;
}
</style>
